import type { SupportedLocale } from "@repo/core";
import { createTranslator, localizedDefaultCategories } from "../i18n/labels";
import type {
  Account,
  Category,
  Debt,
  NetWorthLog,
  SinkingFund,
  Transaction,
} from "./schema";

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function monthsFromNow(months: number, day = 1): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  d.setDate(day);
  return d.toISOString().slice(0, 10);
}

export function buildDemoDataset(
  locale: SupportedLocale = "en",
  opts?: { fullYear?: boolean }
): {
  categories: Category[];
  accounts: Account[];
  debts: Debt[];
  sinkingFunds: SinkingFund[];
  transactions: Transaction[];
  netWorthLogs: NetWorthLog[];
} {
  const t = createTranslator(locale);
  const categories: Category[] = localizedDefaultCategories(locale);
  const household = t("person.household");
  const sideHustle = t("person.sideHustle");

  const accounts: Account[] = [
    { id: "acc-check", name: t("demo.acc-check"), type: "checking", initialBalance: 4200 },
    { id: "acc-sav", name: t("demo.acc-sav"), type: "savings", initialBalance: 14800 },
    { id: "acc-cc1", name: t("demo.acc-cc1"), type: "debt", initialBalance: -2100 },
    { id: "acc-car", name: t("demo.acc-car"), type: "debt", initialBalance: -8200 },
    { id: "acc-inv", name: t("demo.acc-inv"), type: "asset", initialBalance: 32000 },
    { id: "acc-401k", name: t("demo.acc-401k"), type: "asset", initialBalance: 52000 },
  ];

  const debts: Debt[] = [
    { id: "dbt-cc", name: t("demo.dbt-cc"), balance: 2100, interestRate: 19.99, minPayment: 75, extraPayment: 150, color: "coral" },
    { id: "dbt-car", name: t("demo.dbt-car"), balance: 8200, interestRate: 5.9, minPayment: 260, extraPayment: 0, color: "lavender" },
    { id: "dbt-sl", name: t("demo.dbt-sl"), balance: 11800, interestRate: 4.5, minPayment: 150, extraPayment: 50, color: "sky" },
  ];

  const sinkingFunds: SinkingFund[] = [
    { id: "sf-hol", name: t("demo.sf-hol"), targetAmount: 2400, targetDate: monthsFromNow(6, 15), currentSaved: 1200, icon: "✈️", color: "sky" },
    { id: "sf-emg", name: t("demo.sf-emg"), targetAmount: 10000, targetDate: monthsFromNow(9, 1), currentSaved: 6200, icon: "🚨", color: "coral" },
    { id: "sf-car", name: t("demo.sf-car"), targetAmount: 5000, targetDate: monthsFromNow(8, 1), currentSaved: 2100, icon: "🚗", color: "mint" },
    { id: "sf-home", name: t("demo.sf-home"), targetAmount: 3000, targetDate: monthsFromNow(4, 1), currentSaved: 1800, icon: "🔧", color: "lemon" },
    { id: "sf-tech", name: t("demo.sf-tech"), targetAmount: 1800, targetDate: monthsFromNow(3, 1), currentSaved: 900, icon: "💻", color: "lavender" },
  ];

  // Amounts kept ≤ ~105% of target except Dining (deliberate ~115% over in current month)
  const incomeAmounts: Record<string, number[]> = {
    "cat-sal": [5500, 5500, 5500, 5500, 5600, 5500, 5500, 5500, 5500, 5500, 5500, 5500],
    "cat-side": [700, 750, 720, 800, 780, 760, 800, 740, 790, 810, 770, 800],
    "cat-div": [0, 0, 180, 0, 0, 200, 0, 0, 190, 0, 0, 210],
  };
  const expenseAmounts: Record<string, number[]> = {
    "cat-rent": [1400, 1400, 1400, 1400, 1400, 1400, 1400, 1400, 1400, 1400, 1400, 1400],
    "cat-util": [160, 155, 150, 158, 170, 175, 180, 178, 165, 155, 160, 168],
    "cat-groc": [360, 375, 380, 390, 385, 395, 400, 388, 392, 378, 370, 395],
    "cat-ins": [220, 220, 220, 220, 220, 220, 220, 220, 220, 220, 220, 220],
    "cat-trans": [120, 130, 125, 135, 140, 145, 138, 132, 128, 140, 135, 142],
    "cat-health": [70, 60, 80, 75, 85, 70, 65, 90, 80, 75, 70, 85],
    // Deliberate over-budget example (~115% of $200) only in the latest seeded month index
    "cat-eat": [170, 180, 175, 185, 190, 195, 180, 188, 192, 178, 185, 230],
    "cat-ent": [80, 85, 90, 88, 95, 92, 85, 90, 98, 90, 95, 100],
    "cat-shop": [100, 110, 105, 120, 115, 125, 118, 130, 122, 128, 135, 140],
    "cat-sub": [55, 55, 55, 55, 58, 58, 58, 58, 58, 60, 60, 60],
    "cat-travel": [0, 0, 180, 0, 0, 0, 190, 0, 0, 200, 0, 0],
    "cat-emerg": [300, 300, 300, 300, 300, 300, 300, 300, 300, 300, 300, 300],
    "cat-invest": [500, 500, 500, 500, 500, 500, 500, 500, 500, 500, 500, 500],
    "cat-ret": [400, 400, 400, 400, 400, 400, 400, 400, 400, 400, 400, 400],
  };

  const transactions: Transaction[] = [];
  const year = new Date().getFullYear();
  const today = new Date();
  const fullYear = opts?.fullYear === true;

  for (let m = 0; m < 12; m++) {
    const monthDate = new Date(year, m, 1);
    if (!fullYear && monthDate > today) continue;
    const mm = String(m + 1).padStart(2, "0");

    for (const [catId, amounts] of Object.entries(incomeAmounts)) {
      const amount = amounts[m];
      if (!amount) continue;
      transactions.push({
        id: uid(),
        date: `${year}-${mm}-01`,
        type: "income",
        amount,
        categoryId: catId,
        accountId: "acc-check",
        person: catId === "cat-side" ? sideHustle : household,
        notes: "",
      });
    }

    for (const [catId, amounts] of Object.entries(expenseAmounts)) {
      const amount = amounts[m];
      if (!amount) continue;
      const cat = categories.find((c) => c.id === catId)!;
      const accountId = cat.group === "savings" ? "acc-sav" : "acc-check";
      const splits = catId === "cat-groc" || catId === "cat-eat" ? 3 : 1;
      for (let s = 0; s < splits; s++) {
        const day = String(Math.min(28, 3 + s * 8)).padStart(2, "0");
        const diningNotes = [
          t("demo.note.overDining"),
          t("demo.note.workLunch"),
          t("demo.note.weekendOut"),
        ];
        // Dining: short realistic notes (no "Demo:" placeholders)
        const notes =
          catId === "cat-eat" ? diningNotes[s % diningNotes.length] : "";
        transactions.push({
          id: uid(),
          date: `${year}-${mm}-${day}`,
          type: "expense",
          amount: Math.round((amount / splits) * 100) / 100,
          categoryId: catId,
          accountId,
          person: household,
          notes,
        });
      }
    }
  }

  const netWorthLogs: NetWorthLog[] = [];
  for (let m = 0; m < 12; m++) {
    const monthDate = new Date(year, m, 1);
    if (!fullYear && monthDate > today) continue;
    const monthStr = `${year}-${String(m + 1).padStart(2, "0")}`;
    // Deterministic upward trend (no Math.random) for marketing screenshots
    const assets = 78000 + m * 2400;
    const debts = 24000 - m * 700;
    netWorthLogs.push({
      id: uid(),
      monthYear: monthStr,
      assetsTotal: assets,
      debtsTotal: debts,
    });
  }

  return { categories, accounts, debts, sinkingFunds, transactions, netWorthLogs };
}
